import type React from 'react';
import { useState, useEffect } from 'react';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { useAudit } from './audit-context';
import { ProgressBar } from './audit-progress';
import { ImageUpload } from './audit-task-upload';
import { Badge } from 'components/ui/badge';
import { StatusPicker } from './status-picker';
import firebase, { storage } from '../../firebase';
import { toast } from 'sonner';
import { useMultiFileUpload } from 'hooks/useMultiFileUpload';
import firebaseClass from 'firebase/compat/app';
import { useTranslation } from 'react-i18next';
import PageLoader from 'components/Loader/PageLoader';
import { AddSubTask } from './AddSubTaskModal';
import ExtendAuditModal from './audit-extend-modal';
import { auditTypes } from '../InternalAudit/audit-type-selection';
import { AddInspectionTask } from './AddInspectionTaskModal';
import { Separator } from 'components/ui/separator';

export function AuditExecution() {
  const { t } = useTranslation();
  const { audit, tasksMap, subTasksMap, currentTaskIndex, setCurrentTaskIndex, setStep } =
    useAudit();

  if (!audit || !tasksMap) return <PageLoader />;
  const id = Object.keys(tasksMap).find((id) => tasksMap[id]?.order === currentTaskIndex);
  // @ts-ignore
  const currentTask = tasksMap?.[id];

  const isInspection = audit.type.includes('inspection');
  const tasksLength = Object.keys(tasksMap)?.length;

  const handleNextTask = () => {
    if (currentTaskIndex < tasksLength - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setStep('summary');
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const location = audit.location.title;

  const isDisabled = audit.state === 'COMPLETED';
  const auditType = auditTypes?.find((type) => type.id === audit.type);

  console.log({ audit, tasksMap });
  return (
    <div className="flex flex-col items-center space-y-4">
      <h2 className="text-xl font-semibold">{auditType?.title || 'Intern Audit'}</h2>
      <div className="text-muted-foreground">{location}</div>
      {!isDisabled && !audit.type.includes('inspection') && (
        <div className="text-muted-foreground">
          <ExtendAuditModal />
        </div>
      )}
      <div className="w-full max-w-xl space-y-6 ">
        <div className="flex flex-col space-y-8 p-4 border rounded-md">
          <div className="flex justify-between">
            <h3 className="text-lg font-medium">{currentTask?.label}</h3>
            <Badge variant="secondary">{currentTask?.category}</Badge>
          </div>

          {currentTask?.subTaskIds.map((id: string) => {
            return <SubTask key={id} auditId={audit.id} taskId={id} />;
          })}

          {currentTask && (
            <div className="flex w-ful">
              <AddSubTask
                auditId={audit.id}
                taskId={currentTask?.id}
                taskLabel={currentTask?.label}
              />
            </div>
          )}
        </div>
        {/* <Separator /> */}
        {isInspection && (
          <div className="flex justify-end">
            <AddInspectionTask
              auditId={audit.id}
              type={audit.type}
              tasksLength={tasksLength}
              setCurrentTaskIndex={setCurrentTaskIndex}
            />
          </div>
        )}

        {tasksLength > 0 && (
          <div className="w-full flex flex-col items-end justify-center relative space-y-2">
            <ProgressBar progress={((currentTaskIndex + 1) / tasksLength) * 100} />
            <div className=" right-0 -bottom-1 w-[54px] ml-0 text-right text-xs font-medium text-muted-foreground">
              {currentTaskIndex + 1} / {tasksLength}
            </div>
          </div>
        )}
        <div className="flex align-center justify-end gap-2">
          {/* <div>
            <Button variant="outline" onClick={() => alert('SAVE')}>
              Gem Kladde
            </Button>
          </div> */}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={handlePreviousTask}
              disabled={currentTaskIndex === 0}
            >
              Tilbage
            </Button>
            <Button onClick={handleNextTask}>
              {currentTaskIndex < tasksLength - 1 ? 'Næste' : 'Se opsummering'}
            </Button>
          </div>
        </div>

        {audit.completeTime && (
          <div className="flex justify-center text-muted-foreground">
            Afsluttet: {t('general.dateTimeYYYY', { date: audit.completeTime?.toDate() })}
          </div>
        )}
      </div>
    </div>
  );
}

function SubTask({ auditId, taskId }: { auditId: string; taskId: string }) {
  const { audit, subTasksMap } = useAudit();
  const { uploadFiles } = useMultiFileUpload();
  const subTask = subTasksMap?.[taskId];
  const [text, setText] = useState(subTask?.description || '');

  const isDisabled = audit.state === 'COMPLETED';

  useEffect(() => {
    if (!subTask?.description) return;
    setText(subTask?.description);
  }, [subTask]);

  if (!subTask) return null;

  const handleUpdateSubTaskDescription = async (text: string) => {
    try {
      if (!auditId || !taskId) {
        throw new Error(`Missing auditId=${auditId} or taskId=${taskId}`);
      }
      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`subTasksMap.${taskId}.description`]: text,
        });
      toast.success('Beskrivelse opdateret');
    } catch (error) {
      toast.error('Fejl ved opdatering af beskrivelse');
      console.error(error);
    }
  };
  const handleUpdateSubTaskStatus = async (status: string) => {
    try {
      if (!auditId || !taskId) {
        throw new Error(`Missing auditId=${auditId} or taskId=${taskId}`);
      }
      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`subTasksMap.${taskId}.status`]: status,
        });
      toast.success('Status opdateret');
    } catch (error) {
      toast.error('Fejl ved opdatering af status');
      console.error(error);
    }
  };

  const handleUploadAttachments = async (attachments: File[]) => {
    try {
      const files = await uploadFiles(
        attachments,
        `internal-audits/${auditId}/sub-tasks/${taskId}`
      );

      console.log({ files });

      if (!files || files?.some((f) => !f.download)) {
        toast.error('Fejl ved upload af filer');
        return;
      }
      // if (error) {
      //   toast.error('Fejl ved upload af filer');
      //   return;
      // }

      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`subTasksMap.${taskId}.attachments`]: firebaseClass.firestore.FieldValue.arrayUnion(
            ...files
          ),
        });
    } catch (error) {
      console.error(error);
      toast.error('Fejl ved upload af filer');
    }
  };

  const handleRemoveAttachment = async (attachment: any) => {
    console.log({ attachment });

    try {
      await firebase
        .firestore()
        .collection('internalAudits')
        .doc(auditId)
        .update({
          [`subTasksMap.${taskId}.attachments`]:
            firebaseClass.firestore.FieldValue.arrayRemove(attachment),
        });

      // Delete the file from Firebase Storage
      const fileRef = storage.ref(`${attachment.pathRef}/${attachment.fileId}`);
      await fileRef.delete();
      if (attachment.type.startsWith('image')) {
        const thumbRef = storage.ref(`${attachment.pathRef}/thumb_${attachment.fileId}`);
        await thumbRef.delete();
      }
      toast.success('Fil slettet');
    } catch (error) {
      console.error(error);
      toast.error('Fejl ved sletning af fil');
    }
  };

  return (
    <div className="">
      <div className="flex justify-between items-center mb-2 gap-4">
        <div className="">{subTask.label}</div>
        <div>
          <StatusPicker
            disabled={isDisabled}
            status={subTask.status}
            onChange={(status) => handleUpdateSubTaskStatus(status)}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Textarea
          disabled={isDisabled}
          placeholder="Noter bemærkninger..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => handleUpdateSubTaskDescription(text)}
        />
        <div>
          <ImageUpload
            disabled={isDisabled}
            attachments={subTask.attachments}
            onUploadAttachment={handleUploadAttachments}
            onRemoveAttachment={handleRemoveAttachment}
          />
        </div>
      </div>
    </div>
  );
}
